/**
 * CaseSummary — renders a case summary string as markdown.
 *
 * Uses `react-markdown` with GFM (tables, strikethrough, task lists,
 * autolinks) and a component override that opens links in a new tab.
 * Safe by default — `react-markdown` never interprets raw HTML in the
 * source, so no XSS surface from agent-authored summaries. Matches the
 * agent-app implementation so the two C360 renderers stay in sync.
 */
import type { CSSProperties, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface CaseSummaryProps {
  source: string;
  style?: CSSProperties;
  className?: string;
}

export function CaseSummary({ source, style, className }: CaseSummaryProps) {
  return (
    <div className={className} style={{ lineHeight: 1.5, ...style }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }: { href?: string; children?: ReactNode }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ color: '#2563eb', textDecoration: 'underline' }}
            >
              {children}
            </a>
          ),
          // Collapse browser defaults so summaries don't blow up the row.
          p: ({ children }: { children?: ReactNode }) => (
            <p style={{ margin: '0 0 6px' }}>{children}</p>
          ),
          ul: ({ children }: { children?: ReactNode }) => (
            <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>{children}</ul>
          ),
          ol: ({ children }: { children?: ReactNode }) => (
            <ol style={{ margin: '0 0 6px', paddingLeft: 18 }}>{children}</ol>
          ),
          li: ({ children }: { children?: ReactNode }) => (
            <li style={{ margin: '0 0 2px' }}>{children}</li>
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
